import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, TrendingUp, Clock, Percent, CalendarDays, Sparkles, AlertTriangle, ChevronRight } from "lucide-react";
import { API, apiFetch } from "@/utils/api";
import { formatCurrency, formatSignedCurrency } from "@/utils/format";
import { nextQuarterDeadline, formatDeadline } from "@/utils/fiscalDeadline";

export default function MobileHome() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [sumRes, kpiRes, fcRes, fiscalRes, txRes, emittedSummaryRes] = await Promise.all([
        apiFetch(`${API}/dashboard/summary`),
        apiFetch(`${API}/dashboard/kpis`),
        apiFetch(`${API}/dashboard/forecast?months=1`),
        apiFetch(`${API}/fiscal/summary`),
        apiFetch(`${API}/bank/transactions?limit=4`),
        apiFetch(`${API}/invoices/emitted/summary`),
      ]);
      const [summary, kpis, forecast, fiscal, txs, emittedSummary] = await Promise.all(
        [sumRes, kpiRes, fcRes, fiscalRes, txRes, emittedSummaryRes].map((r) => (r.ok ? r.json() : null))
      );
      if (!sumRes.ok) {
        // Sin el summary no hay saldo fiable: mejor error visible que ceros falsos.
        setError(true);
        return;
      }
      setData({ summary, kpis, forecast, fiscal, recent: txs?.transactions ?? [], emittedSummary });
    } catch (e) {
      console.error("Error cargando Inicio móvil", e);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const plazo = nextQuarterDeadline();
  const todayLabel = new Date().toLocaleDateString("es-ES", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  if (loading) {
    return <div className="flex justify-center py-20"><div className="spinner" /></div>;
  }

  if (error) {
    return (
      <div data-testid="mobile-home-error" className="mt-10 bg-white border border-slate-200 rounded-2xl p-5 text-center">
        <p className="text-sm text-slate-700">No se pudieron cargar los datos. Comprueba la conexión.</p>
        <button
          onClick={loadAll}
          className="mt-3 min-h-[44px] px-5 rounded-2xl bg-slate-900 text-white text-sm font-semibold"
        >
          Reintentar
        </button>
      </div>
    );
  }

  const summary = data?.summary;
  const emittedSummary = data?.emittedSummary;
  // Fuente sin borradores (Task 1); si falla la petición, cae al agregado antiguo (con borradores).
  const overdueCount = emittedSummary ? emittedSummary.overdue_count : (data?.kpis?.revenue?.overdue_count ?? 0);
  const pendingAmount = emittedSummary ? emittedSummary.pending_amount_eur : summary?.invoices_to_collect?.amount;
  const pendingCount = emittedSummary ? emittedSummary.pending_count : (summary?.invoices_to_collect?.count ?? 0);
  const overdue = data?.kpis?.revenue?.overdue_invoices?.[0];
  const pendingClassify = summary?.unclassified_transactions ?? 0;
  const projected = data?.forecast?.forecast?.[0]?.projected_balance;

  return (
    <div data-testid="mobile-home">
      {/* Barra superior */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-heading text-lg font-extrabold text-slate-900">LiquidArc</p>
          <p className="text-xs text-slate-500">{todayLabel}</p>
        </div>
        <button
          onClick={() => navigate("/alertas")}
          aria-label="Alertas"
          className="w-11 h-11 flex items-center justify-center bg-white border border-slate-200 rounded-xl"
        >
          <Bell className="w-5 h-5 text-slate-600" strokeWidth={1.5} />
        </button>
      </div>

      {/* Héroe: tesorería */}
      <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
        <p className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
          Tesorería · saldo en banco
        </p>
        <p data-testid="mobile-saldo" className="mt-1 font-mono text-3xl text-slate-900 tabular-nums">
          {formatCurrency(summary?.bank_balance)}
        </p>
        {projected != null && (
          <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
            <TrendingUp className="w-3.5 h-3.5 text-emerald-600" strokeWidth={2} />
            Previsión a fin de mes:{" "}
            <span className="font-mono text-slate-900">{formatCurrency(projected)}</span>
          </p>
        )}
      </div>

      {/* Pendiente de cobro + IVA */}
      <div className="mt-2.5 flex gap-2.5">
        <button
          data-testid="mobile-pendiente-cobro"
          onClick={() => navigate("/facturas/emitidas")}
          className="flex-1 min-w-0 text-left bg-white border border-slate-200 rounded-2xl p-3.5"
        >
          <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            <Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> Pendiente de cobro
          </p>
          <p className="mt-1.5 font-mono text-lg font-semibold text-slate-900 tabular-nums">
            {formatCurrency(pendingAmount)}
          </p>
          <p className="mt-1 text-[11px] text-slate-500">
            {pendingCount} facturas
            {overdueCount > 0 && (
              <> · <span className="text-rose-600 font-semibold">{overdueCount} vencida{overdueCount > 1 ? "s" : ""}</span></>
            )}
          </p>
        </button>
        <button
          data-testid="mobile-iva"
          onClick={() => navigate("/impuestos")}
          className="flex-1 min-w-0 text-left bg-white border border-slate-200 rounded-2xl p-3.5"
        >
          <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            <Percent className="w-3.5 h-3.5 text-amber-600" strokeWidth={1.5} /> IVA a apartar
          </p>
          <p className="mt-1.5 font-mono text-lg font-semibold text-amber-700 tabular-nums">
            {formatCurrency(data?.fiscal?.iva_to_reserve)}
          </p>
          <p className="mt-1 text-[11px] text-slate-500">pendiente de presentar</p>
        </button>
      </div>

      {/* Plazo fiscal */}
      <button
        data-testid="mobile-plazo"
        onClick={() => navigate("/impuestos")}
        className="mt-2.5 w-full text-left bg-amber-50 border border-amber-200 rounded-2xl p-3.5 flex items-center gap-3"
      >
        <span className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
          <CalendarDays className="w-4.5 h-4.5 text-amber-700" strokeWidth={1.5} />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-[13px] font-semibold text-amber-900">
            Presentación {plazo.quarter}T {plazo.year} · modelos 303 y 130
          </span>
          <span className="block text-xs text-amber-800">
            Hasta el <span className="font-mono">{formatDeadline(plazo)}</span> · quedan {plazo.daysLeft} días
          </span>
        </span>
        <ChevronRight className="w-4 h-4 text-amber-600 shrink-0" strokeWidth={1.5} />
      </button>

      {/* Necesita tu atención */}
      {(pendingClassify > 0 || overdue) && (
        <>
          <h2 className="mt-5 mb-2 font-heading text-[15px] font-bold text-slate-900">Necesita tu atención</h2>
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden divide-y divide-slate-100">
            {pendingClassify > 0 && (
              <button
                data-testid="attn-clasificar"
                onClick={() => navigate("/banco/movimientos")}
                className="w-full flex items-center gap-3 px-3.5 py-3 min-h-[44px] text-left"
              >
                <span className="w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4 text-violet-600" strokeWidth={1.5} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[13.5px] font-medium text-slate-900">Movimientos por clasificar</span>
                  <span className="block text-[11.5px] text-slate-500">Las sugerencias esperan — tú confirmas</span>
                </span>
                <span className="min-w-[26px] h-[26px] px-1.5 rounded-full bg-violet-100 text-violet-800 font-mono text-xs font-semibold inline-flex items-center justify-center">
                  {pendingClassify}
                </span>
                <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" strokeWidth={1.5} />
              </button>
            )}
            {overdue && (
              <button
                data-testid="attn-vencida"
                onClick={() => navigate(`/facturas/emitidas?open=${overdue.invoice_id}`)}
                className="w-full flex items-center gap-3 px-3.5 py-3 min-h-[44px] text-left"
              >
                <span className="w-9 h-9 rounded-xl bg-rose-50 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-4 h-4 text-rose-600" strokeWidth={1.5} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[13.5px] font-medium text-slate-900">Factura vencida</span>
                  <span className="block text-[11.5px] text-slate-500 truncate">
                    {overdue.client_name} · <span className="font-mono">{formatCurrency(overdue.pending)}</span> · hace {overdue.days_overdue} días
                  </span>
                </span>
                <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" strokeWidth={1.5} />
              </button>
            )}
          </div>
        </>
      )}

      {/* Últimos movimientos */}
      <div className="mt-5 mb-2 flex items-baseline justify-between">
        <h2 className="font-heading text-[15px] font-bold text-slate-900">Últimos movimientos</h2>
        <button onClick={() => navigate("/banco/movimientos")} className="text-xs font-medium text-blue-600">
          Ver todos
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden divide-y divide-slate-100">
        {data?.recent?.map((tx) => (
          <button
            key={tx.transaction_id}
            data-testid={`recent-item-${tx.transaction_id}`}
            onClick={() => navigate("/banco/movimientos")}
            className="w-full flex items-center justify-between gap-3 px-3.5 py-3 min-h-[44px] text-left"
          >
            <span className="text-[13px] text-slate-700 truncate min-w-0">{tx.description}</span>
            <span className={`font-mono text-[13px] font-semibold whitespace-nowrap tabular-nums ${tx.amount < 0 ? "text-rose-600" : "text-emerald-600"}`}>
              {formatSignedCurrency(tx.amount)}
            </span>
          </button>
        ))}
        {(!data?.recent || data.recent.length === 0) && (
          <p className="px-3.5 py-4 text-sm text-slate-400">Sin movimientos todavía.</p>
        )}
      </div>
    </div>
  );
}
